import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowRight, ShieldCheck, LineChart, FileText, CheckCircle2, Package, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '@/components/AppShell';

const INDUSTRIES = [
  'Accommodation & Hospitality',
  'Agriculture, Forestry & Fishing',
  'Automotive & Transport Services',
  'Building & Construction',
  'Consulting & Professional Services',
  'Creative, Media & Marketing',
  'Education & Training',
  'Financial Services',
  'Health, Wellness & Aged Care',
  'Information Technology & Software',
  'Legal Services',
  'Logistics, Warehousing & Freight',
  'Manufacturing',
  'Mining, Energy & Resources',
  'Not-for-Profit & Community Services',
  'Real Estate & Property',
  'Retail & E-commerce',
  'Tourism, Recreation & Events',
  'Trades & Home Services',
  'Wholesale & Distribution',
  'Other',
];

const WelcomeScreen = ({ onStart, businessInfo }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    business_name: businessInfo?.business_name || '',
    industry: businessInfo?.industry || '',
    business_type: businessInfo?.business_type || ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error('Please accept the Terms and Conditions to continue');
      return;
    }
    if (!formData.business_type) {
      toast.error('Please select whether your business provides goods or services');
      return;
    }
    if (formData.business_name && formData.industry && formData.business_type) {
      onStart(formData);
      navigate('/questionnaire');
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-12 border border-slate-200 rounded-sm overflow-hidden bg-white shadow-sm"
        >
          {/* LEFT — Brand narrative panel */}
          <div className="lg:col-span-5 bg-[#0A192F] text-white p-10 lg:p-14 flex flex-col justify-between relative overflow-hidden">
            {/* subtle grid overlay */}
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{
                backgroundImage:
                  'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)',
                backgroundSize: '40px 40px'
              }}
            />

            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.24em] text-[#7DD3FC] font-semibold mb-4">
                Assessment Tool
              </div>
              <h1 className="font-display text-4xl lg:text-5xl font-light leading-[1.1] mb-6 text-white">
                Business Resilience
                <br />
                <span className="font-semibold">Index</span>
              </h1>
              <p className="text-slate-300 leading-relaxed text-[15px] max-w-md">
                A short, structured assessment that quantifies how resilient your
                business is against competitive, market and operational pressures &mdash;
                benchmarked against your industry.
              </p>
            </div>

            <div className="relative mt-12 space-y-4 pt-8 border-t border-white/10">
              {[
                { icon: LineChart, label: 'Industry benchmarking powered by AI' },
                { icon: ShieldCheck, label: 'Colour-coded risk profile in under 5 minutes' },
                { icon: FileText, label: 'Downloadable executive PDF report' }
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-start gap-3 text-slate-200">
                  <Icon className="w-4 h-4 mt-1 text-[#7DD3FC] flex-shrink-0" strokeWidth={1.75} />
                  <span className="text-sm">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Form panel */}
          <div className="lg:col-span-7 bg-white p-10 lg:p-14">
            <div className="max-w-md">
              <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 font-semibold mb-3">
                Get Started
              </div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A192F] font-medium mb-2">
                Tell us about your business
              </h2>
              <p className="text-sm text-slate-500 mb-10">
                We'll tailor the assessment to your industry and business model.
              </p>

              <form onSubmit={handleSubmit} className="space-y-7">
                {/* Business name */}
                <div className="space-y-2">
                  <Label htmlFor="business_name" className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">
                    Business Name
                  </Label>
                  <Input
                    id="business_name"
                    data-testid="business-name-input"
                    placeholder="e.g. Coastal Timber Co."
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className="h-12 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#0EA5E9] focus-visible:ring-offset-0 focus-visible:border-[#004B87] text-[15px]"
                    required
                  />
                </div>

                {/* Industry */}
                <div className="space-y-2">
                  <Label htmlFor="industry" className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">
                    Industry
                  </Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData({ ...formData, industry: value })}
                  >
                    <SelectTrigger
                      id="industry"
                      data-testid="industry-input"
                      className="h-12 rounded-sm border-slate-300 focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-0 focus:border-[#004B87] text-[15px] shadow-none"
                    >
                      <SelectValue placeholder="Select your industry" />
                    </SelectTrigger>
                    <SelectContent className="rounded-sm max-h-72">
                      {INDUSTRIES.map((ind) => (
                        <SelectItem
                          key={ind}
                          value={ind}
                          data-testid={`industry-option-${ind.replace(/[^A-Za-z0-9]+/g, '-').toLowerCase()}`}
                        >
                          {ind}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Business type */}
                <div className="space-y-3">
                  <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">
                    Business Type
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      data-testid="business-type-goods"
                      onClick={() => setFormData({ ...formData, business_type: 'goods' })}
                      className={`group text-left p-4 rounded-sm border transition-all duration-200 ${
                        formData.business_type === 'goods'
                          ? 'border-[#004B87] bg-[#F0F9FF] ring-1 ring-[#004B87]'
                          : 'border-slate-300 bg-white hover:border-[#0EA5E9] hover:-translate-y-[1px]'
                      }`}
                    >
                      <Package
                        className={`w-5 h-5 mb-2 ${formData.business_type === 'goods' ? 'text-[#004B87]' : 'text-slate-500 group-hover:text-[#0EA5E9]'}`}
                        strokeWidth={1.75}
                      />
                      <div className="font-medium text-[#0A192F] text-sm">Goods</div>
                      <div className="text-[12px] text-slate-500 mt-1 leading-snug">
                        Products, retail, manufacturing
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid="business-type-services"
                      onClick={() => setFormData({ ...formData, business_type: 'services' })}
                      className={`group text-left p-4 rounded-sm border transition-all duration-200 ${
                        formData.business_type === 'services'
                          ? 'border-[#004B87] bg-[#F0F9FF] ring-1 ring-[#004B87]'
                          : 'border-slate-300 bg-white hover:border-[#0EA5E9] hover:-translate-y-[1px]'
                      }`}
                    >
                      <Wrench
                        className={`w-5 h-5 mb-2 ${formData.business_type === 'services' ? 'text-[#004B87]' : 'text-slate-500 group-hover:text-[#0EA5E9]'}`}
                        strokeWidth={1.75}
                      />
                      <div className="font-medium text-[#0A192F] text-sm">Services</div>
                      <div className="text-[12px] text-slate-500 mt-1 leading-snug">
                        Professional services, consulting
                      </div>
                    </button>
                  </div>
                </div>

                {/* T&C */}
                <div className="flex items-start gap-3 pt-2">
                  <Checkbox
                    id="terms"
                    data-testid="terms-checkbox"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    className="mt-1 rounded-sm border-slate-400 data-[state=checked]:bg-[#004B87] data-[state=checked]:border-[#004B87]"
                  />
                  <Label
                    htmlFor="terms"
                    className="text-[13px] font-normal text-slate-600 leading-relaxed cursor-pointer"
                  >
                    I have read and accept the{' '}
                    <Dialog>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          className="text-[#004B87] font-semibold hover:text-[#0A192F] underline underline-offset-2"
                          data-testid="view-terms-button"
                        >
                          Terms and Conditions
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-sm">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2 text-2xl font-display">
                            <FileText className="w-6 h-6 text-[#004B87]" strokeWidth={1.75} />
                            Terms and Conditions of Use
                          </DialogTitle>
                          <DialogDescription>
                            EYB Consulting Pty Ltd ("we", "us", or "our")
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 text-sm text-slate-700 py-4">
                          <section>
                            <h3 className="font-semibold text-base mb-2">1. Acceptance of Terms</h3>
                            <p>By accessing or using this Business Resilience Index Tool ("the Tool"), you agree to be bound by these Terms and Conditions. If you do not agree, please do not use the Tool.</p>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">2. Purpose of the Tool</h3>
                            <p>The Tool is designed to help businesses assess and understand their operational, financial, and strategic risks. It provides general insights based on user inputs and should not be relied upon as professional, financial, or legal advice.</p>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">3. No Financial Product Advice</h3>
                            <p>The Tool does not provide financial product advice under the Corporations Act 2001 (Cth). All risk assessments are indicative and for informational purposes only. Users should seek independent professional advice before making business, investment, or financial decisions.</p>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">4. Use of Information</h3>
                            <p>You are responsible for ensuring that all information entered into the Tool is accurate, complete, and up to date. The Tool's output depends entirely on the quality and accuracy of the data you provide. We are not responsible for any errors, omissions, or inaccuracies arising from user input.</p>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">5. Intellectual Property</h3>
                            <p>All content, design, logic, and output of the Tool are the intellectual property of EYB Consulting Pty Ltd. You may not copy, reproduce, distribute, or modify any part of the Tool or its results without our written consent.</p>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">6. Privacy and Data Use</h3>
                            <p>We may collect and store information that you provide when using the Tool. Any data collected will be handled in accordance with our Privacy Policy. We do not sell or share user data with third parties except where required by law or where necessary to provide our services.</p>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">7. Marketing and Communications</h3>
                            <p>By using the Tool and providing your contact details, you acknowledge and agree that EYB Consulting Pty Ltd may contact you from time to time with tailored offers, updates, insights, promotional materials, and other communications relating to our services. You may opt out of these communications at any time by emailing us at <a href="mailto:info@eybconsulting.com.au" className="text-[#004B87] hover:underline">info@eybconsulting.com.au</a>.</p>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">8. Disclaimer and Limitation of Liability</h3>
                            <ul className="list-disc pl-5 space-y-2">
                              <li>The Tool provides general insights and is not a substitute for professional advice.</li>
                              <li>While reasonable efforts are made to ensure the Tool functions correctly, we make no guarantees regarding accuracy, completeness, reliability, or availability.</li>
                              <li>To the fullest extent permitted by law, EYB Consulting Pty Ltd, including its directors, consultants, employees, and agents, is not liable for any loss, damage, or costs arising from the use of or reliance on the Tool or its outputs, whether direct, indirect, consequential, or incidental.</li>
                              <li>No consultant, director, employee, or representative of EYB Consulting Pty Ltd shall be personally liable for any claims or damages arising from or connected to the use of this Tool.</li>
                              <li>Your use of the Tool is entirely at your own risk.</li>
                            </ul>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">9. User Conduct</h3>
                            <p className="mb-2">You agree not to:</p>
                            <ul className="list-disc pl-5 space-y-2">
                              <li>Use the Tool for unlawful, misleading, or fraudulent purposes.</li>
                              <li>Attempt to reverse engineer, copy, or disrupt the Tool's functionality.</li>
                              <li>Input any material that is offensive, defamatory, or infringes on the rights of others.</li>
                            </ul>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">10. Availability and Access</h3>
                            <p>We reserve the right to modify, suspend, or discontinue the Tool at any time without prior notice. We are not responsible for any downtime or access interruptions.</p>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">11. Modifications to Terms</h3>
                            <p>We may update these Terms and Conditions from time to time. The latest version will be made available before tool access, and continued use indicates acceptance of the revised terms.</p>
                          </section>
                          <section>
                            <h3 className="font-semibold text-base mb-2">12. Governing Law</h3>
                            <p>These Terms and Conditions are governed by the laws of Victoria, Australia. Any disputes arising under these terms will be subject to the exclusive jurisdiction of the courts of Victoria.</p>
                          </section>
                          <section className="pt-4 border-t">
                            <h3 className="font-semibold text-base mb-2">13. Contact Information</h3>
                            <p className="mb-1">For questions or concerns, please contact:</p>
                            <p className="font-medium">EYB Consulting Pty Ltd</p>
                            <p>Email: <a href="mailto:info@eybconsulting.com.au" className="text-[#004B87] hover:underline">info@eybconsulting.com.au</a></p>
                          </section>
                        </div>
                      </DialogContent>
                    </Dialog>
                    {' '}and understand that this tool provides indicative risk assessment only.
                  </Label>
                </div>

                {/* CTA */}
                <Button
                  type="submit"
                  data-testid="start-assessment-button"
                  disabled={!termsAccepted}
                  className="w-full h-12 rounded-sm bg-[#004B87] hover:bg-[#0A192F] text-white text-[13px] uppercase tracking-[0.18em] font-semibold shadow-none transition-colors disabled:opacity-40 disabled:cursor-not-allowed group"
                >
                  Start Assessment
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                </Button>

                <div className="flex items-center gap-2 text-[12px] text-slate-500 pt-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  Takes less than 5 minutes.
                </div>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </AppShell>
  );
};

export default WelcomeScreen;
